import { useCallback, useEffect, useState } from 'react'
import { ROLE, useAuth } from '../../auth'
import { IsGranted } from '../../auth/PrivateRoute'
import { getProtectedAccounts } from '../api/secondFactor'

/** `{passkeys}` — protected; `null` — not protected; `undefined` — not known (not loaded, load failed). */
export type StaffSecondFactor = { passkeys: number } | null | undefined

/**
 * The second factor of the accounts in the staff list — for the roles that may see and remove it.
 * A failed load leaves every account "not known": the list itself must not depend on it.
 */
export const useSecondFactorUserIds = () => {
  const { currentUser } = useAuth()
  const allowed = IsGranted([ROLE.ROLE_ADMIN, ROLE.ROLE_MANAGER], currentUser)
  const [accounts, setAccounts] = useState<Map<number, number> | null>(null)

  const reload = useCallback(async () => {
    if (!allowed) {
      return
    }

    try {
      const res = await getProtectedAccounts()
      if (!res.data.success) {
        setAccounts(null)
        return
      }

      const { accounts: list, userIds } = res.data.data
      setAccounts(new Map((list ?? userIds.map((userId) => ({ userId, passkeys: 0 }))).map((a) => [a.userId, a.passkeys])))
    } catch {
      setAccounts(null)
    }
  }, [allowed])

  useEffect(() => {
    reload()
  }, [reload])

  const of = (userId: number): StaffSecondFactor => {
    if (accounts === null) {
      return undefined
    }

    return accounts.has(userId) ? { passkeys: accounts.get(userId) ?? 0 } : null
  }

  return { allowed, of, reload }
}
